/**
 * MyTradeScorePanel — Iter 160 (L3 + L4)
 *
 * Buyer-facing self-view of their Verqo Trade Score. Full transparency
 * (every factor visible), with the two legal-shield actions:
 *
 *   L3 · File a dispute — flags the score as "Disputed — under review."
 *   L4 · Opt out of seller visibility — sellers see "Score withheld."
 *
 * Drop this into Buyer Settings (new "Trade Score" tab) or the
 * /buyer/score deep-link landing page.
 */
import { useEffect, useState } from "react";
import { ShieldAlert, ExternalLink, AlertCircle, Check, Layers, Building2, Banknote, FileText, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import TradeScoreBadge from "./TradeScoreBadge";

export default function MyTradeScorePanel() {
  const [data, setData]       = useState(null);
  const [reason, setReason]   = useState("");
  const [filing, setFiling]   = useState(false);

  const load = () => api.get("/trade-score/me").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitDispute = async () => {
    if (reason.trim().length < 5) return toast.error("Reason must be at least 5 characters");
    setFiling(true);
    try {
      await api.post("/trade-score/me/dispute", { reason: reason.trim() });
      toast.success("Sent to Verqo ops — we'll investigate and email you back");
      setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not file dispute");
    } finally { setFiling(false); }
  };

  if (!data) return <div className="text-[12.5px] text-foreground/55">Loading your Trade Score…</div>;

  return (
    <div className="space-y-5" data-testid="my-trade-score-panel">
      <div className="rounded-2xl border border-border bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">Your Verqo Trade Score</div>
            <div className="text-[12.5px] text-foreground/65">Informational signal sellers see when reviewing you.</div>
          </div>
          <TradeScoreBadge data={data}/>
        </div>
        {data.factors && (
          <div className="grid grid-cols-2 gap-3 mt-4" data-testid="trade-score-factors">
            <Kv label="Paid invoices"      value={data.factors.paid_invoices_count}/>
            <Kv label="On-time payment %"  value={data.factors.on_time_payment_pct ?? "—"}/>
            <Kv label="Avg days late"      value={data.factors.avg_days_late ?? 0}/>
            <Kv label="Open invoices"      value={data.factors.open_invoices_count}/>
            <Kv label="Outstanding"        value={`$${(data.factors.outstanding_balance || 0).toLocaleString()}`}/>
            <Kv label="Confidence"         value={data.confidence}/>
          </div>
        )}
        <div className="text-[11px] text-foreground/55 mt-4 leading-snug">
          {data.disclaimer_full}
        </div>
        <a
          href="/legal/trade-score"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider text-foreground hover:underline mt-2"
          data-testid="trade-score-methodology-link"
        >
          Read methodology {data.methodology} <ExternalLink className="w-3 h-3"/>
        </a>
      </div>

      {/* Iter 163 — Two-track engine: "What's feeding your score" */}
      <WhatsFeedingYourScore data={data}/>

      {/* L3 — Dispute (Iter 168 · tightened framing) */}
      <div className="rounded-2xl border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-foreground/60"/>
          <div className="text-[13px] font-medium">Think something&apos;s wrong?</div>
        </div>
        {data.disputed ? (
          <div className="text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 inline-flex items-center gap-2" data-testid="trade-score-disputed-banner">
            <AlertCircle className="w-3.5 h-3.5"/>We&apos;ve received your note — Verqo ops is investigating. We&apos;ll email you with our findings.
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-border bg-secondary/30 p-3.5 mb-3 text-[12px] text-foreground/75 leading-relaxed space-y-2" data-testid="trade-score-dispute-explainer">
              <div className="flex gap-2">
                <span className="text-foreground/45 mono shrink-0">1.</span>
                <span>
                  Your Verqo Trade Score is built <strong>automatically</strong> from real activity inside the portal — invoices, payments, on-time history. We don&apos;t enter any data by hand.
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-foreground/45 mono shrink-0">2.</span>
                <span>
                  We <strong>don&apos;t score personal data</strong>. Verqo is not a credit-reporting agency under the FCRA. This is a trade-trust signal between you and the sellers you choose to work with.
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-foreground/45 mono shrink-0">3.</span>
                <span>
                  External data comes from your <strong>bank connection</strong> (Plaid/Flinks) and <strong>state registries</strong> (Middesk) — there&apos;s not much room for that to be wrong on our side.
                </span>
              </div>
              <div className="pt-1 mt-1 border-t border-border/60 text-foreground/65">
                If you still believe a specific factor is incorrect, tell us below. We&apos;ll investigate and get back to you.
              </div>
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="trade-score-dispute-reason"
              placeholder="Which specific factor or transaction looks wrong? (e.g. 'Invoice INV-1234 was paid via wire on 5/12 but still shows unpaid')"
              className="w-full min-h-[80px] rounded-lg border border-border bg-secondary/30 p-2.5 text-[13px] mb-3"
            />
            <Button
              onClick={submitDispute}
              disabled={filing || reason.trim().length < 5}
              className="rounded-full"
              data-testid="trade-score-dispute-submit"
            >
              {filing ? "Sending…" : (<><Check className="w-3.5 h-3.5 mr-1"/>Send for investigation</>)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="text-[10px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className="text-[14px] font-medium mt-0.5">{value}</div>
    </div>
  );
}


/* WhatsFeedingYourScore — Iter 163 · two-track engine transparency panel.
 *
 * Shows the buyer EXACTLY which sources fed their score (Middesk, Plaid,
 * Experian, D&B) + the maturity progress bar so the thin-file → established
 * journey feels like a progress quest, not a limitation. */
function WhatsFeedingYourScore({ data }) {
  const ext = data?.factors_v2?.external;
  const mix = data?.data_source_mix;
  if (!ext || !mix) return null;

  const md = ext.middesk || {};
  const cf = ext.cashflow || {};
  const exp = ext.experian || {};
  const dnb = ext.dnb || {};

  const sources = [
    {
      id: "middesk", icon: Building2, label: "Middesk · KYB",
      available: md.available,
      lines: md.available
        ? [
            `Status: ${md.kyb_status || "—"}`,
            md.business_age_months != null ? `Business age: ${md.business_age_months}mo` : null,
            md.tax_liens > 0 ? `${md.tax_liens} active tax lien${md.tax_liens > 1 ? "s" : ""}` : null,
            md.judgments > 0 ? `${md.judgments} active judgment${md.judgments > 1 ? "s" : ""}` : null,
          ].filter(Boolean)
        : ["Not yet pulled — complete KYB to add this signal."],
    },
    {
      id: "cashflow", icon: Banknote, label: "Plaid / Flinks · Cashflow",
      available: cf.available,
      lines: cf.available
        ? [
            cf.institution ? `Bank: ${cf.institution}` : null,
            cf.months_of_data != null ? `${cf.months_of_data}mo of data` : null,
            `${cf.nsf_count_90d || 0} NSF events · last 90d`,
            cf.avg_monthly_revenue ? `~$${Math.round(cf.avg_monthly_revenue).toLocaleString()}/mo revenue` : null,
          ].filter(Boolean)
        : ["Not yet connected — link your bank to add this signal."],
    },
    {
      id: "experian", icon: FileText, label: "Experian Business",
      available: exp.available,
      mocked: exp.is_mock,
      lines: exp.available
        ? [
            exp.intelliscore_plus != null ? `Intelliscore Plus: ${exp.intelliscore_plus}/100` : null,
            exp.dbt_days != null ? `Days Beyond Terms: ${exp.dbt_days}` : null,
            exp.risk_class ? `Risk class: ${exp.risk_class}` : null,
          ].filter(Boolean)
        : ["No bureau pull on file yet."],
    },
    {
      id: "dnb", icon: TrendingUp, label: "Dun & Bradstreet",
      available: dnb.available,
      mocked: dnb.is_mock,
      lines: dnb.available
        ? [
            dnb.paydex != null ? `PAYDEX: ${dnb.paydex}/100` : null,
          ].filter(Boolean)
        : ["No D&B pull on file yet."],
    },
  ];

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="whats-feeding-score">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 flex items-center gap-1.5">
            <Layers className="w-3 h-3"/>What's feeding your score
          </div>
          <div className="text-[13px] text-foreground/65 mt-0.5">
            Your score blends two tracks. As you transact on Verqo, behavioral signals carry more weight.
          </div>
        </div>
      </div>

      {/* Maturity progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-1.5">
          <span>External signals · {mix.external_pct}%</span>
          <span>Behavioral · {mix.behavioral_pct}%</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden flex bg-foreground/[0.06]" data-testid="maturity-bar">
          <div className="bg-sky-500 transition-all" style={{ width: `${mix.external_pct}%` }}/>
          <div className="bg-emerald-500 transition-all" style={{ width: `${mix.behavioral_pct}%` }}/>
        </div>
        <div className="text-[11px] text-foreground/55 mt-2">
          {mix.paid_invoices === 0
            ? "Pay your first invoice on Verqo to unlock the behavioral track."
            : mix.paid_invoices < 12
              ? `${mix.paid_invoices} paid invoice${mix.paid_invoices > 1 ? "s" : ""} · pay ${12 - mix.paid_invoices} more to reach "Established" maturity (35% external · 65% behavioral).`
              : `${mix.paid_invoices} paid invoices · you're in the "Established" band where behavioral signals dominate.`}
        </div>
      </div>

      {/* Source breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="external-sources">
        {sources.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.id}
                 className={`rounded-lg border p-3 ${s.available ? "border-border bg-secondary/20" : "border-dashed border-foreground/15 bg-foreground/[0.02]"}`}
                 data-testid={`external-source-${s.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`w-3.5 h-3.5 ${s.available ? "text-foreground/70" : "text-foreground/35"}`}/>
                <div className="text-[12.5px] font-medium flex-1">{s.label}</div>
                {s.available ? (
                  <span className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-wider text-emerald-700">
                    <Check className="w-2.5 h-2.5"/>Connected
                  </span>
                ) : (
                  <span className="text-[9.5px] mono uppercase tracking-wider text-foreground/45">Not yet</span>
                )}
                {s.mocked && (
                  <span className="text-[8.5px] mono uppercase tracking-wider px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    mock
                  </span>
                )}
              </div>
              <ul className="text-[11.5px] text-foreground/65 space-y-0.5 ml-5">
                {s.lines.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
